import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Avatar,
  Form,
  Input,
  Select,
  Upload,
  Typography,
  Checkbox,
} from "antd";
import {
  UserOutlined,
  SaveOutlined,
  PhoneOutlined,
  CommentOutlined,
  UploadOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { TbCurrencyRupee } from "react-icons/tb";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserDeatilsbyId } from "../../redux/usersList/userAction";
import { createConsultantnoRedux } from "../../redux/consultant/consultantAction";
import { uploadImageToCloudinary } from "../../utils/imageUpload";
import { ToastContainer, toast } from "react-toastify";
import { motion } from "framer-motion";
import { consultantCategory } from "../../components/consultantCategory";
import { primaryColor } from "../../utils/constants";

const { Option } = Select;
const { Title, Text } = Typography;

function ConsultantCreate() {
  const { activeUser, loading } = useSelector((state) => state.user);
  const createdForm = useSelector(
    (state) => state.consultant?.consultantCreated?.statusText
  );
  const navigate = useNavigate();
  const { id } = useParams();
  const dispatch = useDispatch();

  // Theme color
  // const primaryColor = '#A40165';

  // State
  const [formData, setFormData] = useState({
    id: id,
    phone: "",
    about: "",
    availability: "",
    pricePerMinute: "",
    communicationType: [],
    image: "",
    firstName: "",
    lastName: "",
    category: "",
  });
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [animateProfile, setAnimateProfile] = useState(false);

  // Form
  const [form] = Form.useForm();

  useEffect(() => {
    if (id) {
      dispatch(getUserDeatilsbyId(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (createdForm === "Created") {
      navigate("/consultant-profile");
    }
  }, [createdForm, navigate]);

  // Animation effect
  useEffect(() => {
    setTimeout(() => {
      setAnimateProfile(true);
    }, 100);
  }, []);

  // Handle form field changes
  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image upload
  const handleImageChange = (file) => {
    setImage(file);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    return false; // Prevent auto upload
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Validate form
      await form.validateFields();

      // Prepare data
      const updatedData = { ...formData };

      // Upload image if provided
      if (image) {
        const imageUrl = await uploadImageToCloudinary(image);
        updatedData.image = imageUrl;
      }

      // Submit to API
      const result = await createConsultantnoRedux(updatedData);

      if (result.data) {
        toast.success("Consultant created successfully");
        navigate("/consultant-profile");
      } else {
        toast.error(
          result.response?.data?.message || "Failed to create consultant"
        );
      }
    } catch (error) {
      console.error("Error creating consultant:", error);
      toast.error("Failed to create consultant");
    } finally {
      setIsSubmitting(false);
    }
  };
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5 },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="p-4 sm:p-6 lg:p-8"
    >
      <ToastContainer />

      {/* Back button */}
      <div className="mb-6">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/consultant-profile")}
          className="flex items-center"
          style={{ color: primaryColor }}
        >
          Back to Consultants
        </Button>
      </div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: animateProfile ? 1 : 0,
          y: animateProfile ? 0 : 20,
        }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-2xl overflow-hidden mb-6"
        style={{ boxShadow: "0 4px 20px rgba(164, 1, 101, 0.07)" }}
      >
        {/* Header background */}
        <div
          className="h-28 relative overflow-hidden"
          style={{
            background: `linear-gradient(135deg, ${primaryColor}CC 0%, ${primaryColor}99 100%)`,
          }}
        >
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-1/3 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full transform -translate-x-1/3 translate-y-1/2"></div>
        </div>

        {/* Header content */}
        <div className="px-6 pb-6 relative">
          <div className="flex flex-col md:flex-row items-start md:items-center">
            {/* Avatar */}
            <div className="relative -mt-14 mb-4 md:mb-0">
              <div
                className="absolute inset-0 rounded-full opacity-20"
                style={{
                  background: `${primaryColor}10`,
                  transform: "scale(1.15)",
                }}
              ></div>

              <Upload
                accept="image/*"
                showUploadList={false}
                beforeUpload={handleImageChange}
              >
                <div className="relative cursor-pointer">
                  <Avatar
                    size={110}
                    src={imagePreview}
                    icon={
                      !imagePreview && (
                        <UserOutlined style={{ color: "#888" }} />
                      )
                    }
                    className="border-3 border-white shadow-md"
                    style={{
                      boxShadow: `0 5px 15px rgba(164, 1, 101, 0.1)`,
                      filter: imagePreview ? "none" : "brightness(0.8)",
                    }}
                  />
                  {!imagePreview && (
                    <div
                      className="absolute inset-0 flex items-center justify-center rounded-full"
                      style={{
                        backgroundColor: "rgba(0, 0, 0, 0.3)",
                        zIndex: 5,
                      }}
                    >
                      <UploadOutlined
                        style={{
                          fontSize: "32px",
                          color: "white",
                          opacity: 0.9,
                        }}
                      />
                    </div>
                  )}
                </div>
              </Upload>
            </div>

            {/* Title */}
            <div className="md:ml-6 flex-1">
              <Title
                level={3}
                style={{ margin: 0, fontWeight: 400, color: "#333" }}
              >
                Create New Consultant
              </Title>
              <Text type="secondary">
                Fill in the details to create a new consultant profile
              </Text>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Form Content */}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={[24, 24]}>
          {/* Left Column */}
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: animateProfile ? 1 : 0,
                y: animateProfile ? 0 : 20,
              }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card
                className="rounded-xl shadow-md overflow-hidden"
                title={
                  <div className="flex items-center">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
                      style={{ background: `${primaryColor}15` }}
                    >
                      <UserOutlined style={{ color: primaryColor }} />
                    </div>
                    <span>Personal Information</span>
                  </div>
                }
                headStyle={{ borderBottom: `1px solid ${primaryColor}20` }}
                bodyStyle={{ padding: "20px" }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="firstName"
                      label="First Name"
                      rules={[
                        { required: true, message: "Please enter first name" },
                      ]}
                    >
                      <Input
                        placeholder="Enter first name"
                        onChange={(e) =>
                          handleChange("firstName", e.target.value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="lastName"
                      label="Last Name"
                      rules={[
                        { required: true, message: "Please enter last name" },
                      ]}
                    >
                      <Input
                        placeholder="Enter last name"
                        onChange={(e) =>
                          handleChange("lastName", e.target.value)
                        }
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone"
                      label="Phone Number"
                      rules={[
                        {
                          required: true,
                          message: "Please enter phone number",
                        },
                        {
                          pattern: /^\d{10}$/,
                          message: "Please enter a valid 10-digit phone number",
                        },
                      ]}
                    >
                      <Input
                        placeholder="Enter phone number"
                        maxLength={10}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        prefix={<PhoneOutlined className="text-gray-400" />}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} md={12}>
                    <Form.Item
                      name="category"
                      label="Category"
                      rules={[
                        { required: true, message: "Please select a category" },
                      ]}
                    >
                      <Select
                        placeholder="Select category"
                        onChange={(value) => handleChange("category", value)}
                        showSearch
                        optionFilterProp="children"
                      >
                        {consultantCategory.map((item, index) => (
                          <Option key={index} value={item.value}>
                            {item.title}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="about"
                      label="About"
                      rules={[
                        { required: true, message: "Please enter description" },
                      ]}
                    >
                      <Input.TextArea
                        placeholder="Enter consultant description"
                        rows={4}
                        onChange={(e) => handleChange("about", e.target.value)}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24}>
                    <Form.Item
                      name="availability"
                      label="Availability"
                      rules={[
                        {
                          required: true,
                          message: "Please enter availability",
                        },
                      ]}
                    >
                      <Input
                        placeholder="e.g., Mon-Fri 9AM-6PM"
                        onChange={(e) =>
                          handleChange("availability", e.target.value)
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </motion.div>
          </Col>

          {/* Right Column */}
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: animateProfile ? 1 : 0,
                y: animateProfile ? 0 : 20,
              }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Card
                className="rounded-xl shadow-md overflow-hidden mb-6"
                title={
                  <div className="flex items-center">
                    <div
                      className="flex items-center justify-center w-8 h-8 rounded-full mr-3"
                      style={{ background: `${primaryColor}15` }}
                    >
                      <CommentOutlined style={{ color: primaryColor }} />
                    </div>
                    <span>Service Settings</span>
                  </div>
                }
                headStyle={{ borderBottom: `1px solid ${primaryColor}20` }}
                bodyStyle={{ padding: "20px" }}
              >
                <Form.Item
                  name="pricePerMinute"
                  label="Price Per Minute (₹)"
                  rules={[
                    {
                      required: true,
                      message: "Please enter price per minute",
                    },
                    {
                      validator: (_, value) => {
                        const num = Number(value);
                        if (isNaN(num) || num < 0 || num > 250) {
                          return Promise.reject(
                            "Price must be between 0 and 250"
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input
                    type="number"
                    min={0}
                    max={250}
                    onChange={(e) =>
                      handleChange("pricePerMinute", Number(e.target.value))
                    }
                    addonBefore={<TbCurrencyRupee />}
                    suffix="per minute"
                  />
                </Form.Item>

                <Form.Item
                  name="communicationType"
                  label="Communication Types"
                  rules={[
                    {
                      required: true,
                      message: "Please select at least one communication type",
                    },
                  ]}
                >
                  <Checkbox.Group
                    onChange={(values) =>
                      handleChange("communicationType", values)
                    }
                  >
                    <div className="flex flex-col gap-2">
                      <Checkbox value="phone">Phone Call</Checkbox>
                      <Checkbox value="chat">Chat</Checkbox>
                    </div>
                  </Checkbox.Group>
                </Form.Item>
              </Card>

              {/* Submit Button */}
              <div className="flex justify-end">
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={isSubmitting}
                  size="large"
                  style={{
                    backgroundColor: primaryColor,
                    borderColor: primaryColor,
                    height: "48px",
                    paddingLeft: "32px",
                    paddingRight: "32px",
                  }}
                >
                  Create Consultant
                </Button>
              </div>
            </motion.div>
          </Col>
        </Row>
      </Form>

      {/* Custom styles */}
      <style jsx="true">{`
        .ant-form-item-label > label {
          color: #666;
          font-weight: 500;
        }

        .ant-select-focused:not(.ant-select-disabled).ant-select:not(
            .ant-select-customize-input
          )
          .ant-select-selector {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .ant-input:focus,
        .ant-input-focused {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}20 !important;
        }

        .ant-input:hover {
          border-color: ${primaryColor}80 !important;
        }

        .ant-checkbox-checked .ant-checkbox-inner {
          background-color: ${primaryColor} !important;
          border-color: ${primaryColor} !important;
        }

        .ant-checkbox-wrapper:hover .ant-checkbox-inner {
          border-color: ${primaryColor} !important;
        }
      `}</style>
    </motion.div>
  );
}

export default ConsultantCreate;
